/*
 * ImportCsvAlkis.tsx
 * Author: rmueller-siegert
 * Date: 18.10.2023
 *
 * Copyright: DMT GmbH & Co. KG
 */

import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useForm} from 'react-hook-form';
import {FileUpload} from 'primereact/fileupload';
import {InputSwitch} from 'primereact/inputswitch';
import keycloaksocket from '../../../functions/keycloaksocket';
import settings from '../../../config/settings';
import {useSelector} from 'react-redux';
import {setUploadStatus, resetUploadStatus, startUpload, setImportState} from '../../../redux/slices/ImportSlice';
import {useAppDispatch} from '../../../redux/hooks';
import {showMessageOnError, showMessageOnSuccess} from '../../global/CustomToast';
import { Button } from 'primereact/button';
import {Tooltip} from 'primereact/tooltip';
import {CustomConfirmDialog} from '../../global/CustomConfirmDialog';
import CustomDialog from '../../global/CustomDialog';
import {useGetParcelUseTypesQuery, useGetRegisterUpdateTypesQuery} from '../../../redux/rtk/injectedParcelApi';
import {useGetLegalFormTypesQuery, useGetLegalTypesQuery} from "../../../redux/rtk/injectedPersonApi";
import {useGetGrundbuchTypesQuery} from "../../../redux/rtk/injectedLandregisterApi";

type Props = {
    id: string,
    projectId: string,
    onSubmit: (e: any) => void
}

/**
 * Form for editing and creating of users
 * @param props
 * @constructor
 */
export const ImportCsvAlkis = (props: Props): JSX.Element => {

    const {t} = useTranslation(['common', 'globalData', 'toasts']);
    const dispatch = useAppDispatch();

    const defaultValues = {'role_id': ''};
    const {control, formState: {errors}, handleSubmit, getValues} = useForm({defaultValues});
    const [checkedPerson, setCheckedPerson] = useState<boolean>(true);
    const [showInfoDialog, setShowInfoDialog] = useState<boolean>(false);
    const fileUploadRef = useRef(null);
    const status = useSelector((state: any) => state);
    const [uploadedFile, setUploadedFile] = useState<any>(null);
    const [parcelUseType, setParcelUseType] = useState<any>('');
    const [katasterFortschreibungType, setKatasterFortschreibungType] = useState<any>('');
    const [legalTypes, setLegalTypesType] = useState<any>('');
    const [legalFormTypes, setLegalFormTypes] = useState<any>('');
    const [grundbuchTypes, setGrundbuchTypes] = useState<any>('');
    const {data: parcelUseTypesQuery = []} = useGetParcelUseTypesQuery();
    const {data: katasterFortschreibungTypesQuery = []} = useGetRegisterUpdateTypesQuery();
    const {data: legalTypesQuery = []} = useGetLegalTypesQuery();
    const {data: legalFormTypesQuery = []} = useGetLegalFormTypesQuery();
    const {data: typesFetchQuery = []} = useGetGrundbuchTypesQuery({});

    const customBase64Uploader = async (event: { files: any[]; }) => {

        const file = event.files[0];
        const reader = new FileReader();

        // const blob = await fetch(file.objectURL).then((r) => r.blob()); // blob:url
        // reader.readAsText(file, 'US-ASCII');
        reader.readAsText(file, 'Windows-1252');
        // reader.readAsText(file, 'UTF-8');
        // reader.readAsDataURL(blob);

        reader.onload = function () {
            console.log('encoding', 'Windows-1252');

            setUploadedFile(reader.result);

            // saveCSV(props.projectId, reader.result, true, true
            // ).then(result => {
            //     if (result.status==='ok') {
            //         showMessageOnSuccess(t('success'), t('globalData:global.getCSVDialog.fileSaved'));
            //     } else {
            //         showMessageOnError(t('error'), t('errorMessages:' + result.status));
            //     }
            //     // @ts-ignore
            //     fileUploadRef.current.clear();
            // }).catch((err: any) => {
            //     console.log('error', err);
            // });

        };
    };

    useEffect(() => {
        // console.log(uploadedFile)
    }, [uploadedFile]);

    const importData = (data: any) => {

        const socket = keycloaksocket.init(settings.apiUrl, status).then((socket: any) => {

            dispatch(startUpload());

            const _data = {
                'project_id': props.projectId,
                'data': uploadedFile,
                'overwrite_person': false,
                'overwrite_parcel': false
            };
            keycloaksocket.connect(socket, _data, 'importALKIS', status);
            keycloaksocket.status(socket, 'Status', 'OK', (result: any) => {

                try {
                    const data = JSON.parse(result);
                    if (data.function === 'makeImportALKIS') {
                        dispatch(setUploadStatus(data));
                    }
                } catch (e) {
                    dispatch(resetUploadStatus(false));
                }

                if (result === 'OK') {
                    showMessageOnSuccess('Upload', t('globalData:global.uploadCompleted'));
                    dispatch(resetUploadStatus(true));
                } else if (result.indexOf('Error') >= 0) {
                    dispatch(resetUploadStatus(false));
                    showMessageOnError(t('toasts:error'), result);
                }

                console.log('Result:', result);
            });
        });

        console.log(JSON.stringify(socket));

        props.onSubmit(data);
    };

    const generateTable=(data:any, h1:string, h2:string, header:string)=> {
        return (<div className="tableTypes">
            <h5>{header}</h5>
            <table style={{width:"100%", border:"1px solid"}}>
                <tr style={{textAlign:"left"}}>
                    <th style={{width:"10%"}}>{h1}</th>
                    <th>{h2}</th>
                </tr>
                {data.map((val:any, key:any) => {
                    return (
                        <tr key={key} >
                            <td>{val.id}</td>
                            <td>{val.name}</td>
                        </tr>
                    );
                })}
            </table>
        </div>);
    };

    const hideInfoDialog = () => {
        setShowInfoDialog(false);
    };


    const showTheInfoDialog = (e: any) => {
        setShowInfoDialog(true);
    };

    useEffect(() => {
        if (parcelUseTypesQuery.length>0) {
            const parcelTypeTable = generateTable(parcelUseTypesQuery, 'id', 'name', 'liegenschaft_nutzung_types');
            setParcelUseType(parcelTypeTable);
        }
    }, [parcelUseTypesQuery]);


    useEffect(() => {
        if (katasterFortschreibungTypesQuery.length>0) {
            const katasterTypeTable = generateTable(katasterFortschreibungTypesQuery, 'id', 'name', 'katasterfortschreibung_type');
            setKatasterFortschreibungType(katasterTypeTable);
        }
    }, [katasterFortschreibungTypesQuery]);

    useEffect(() => {
        if (legalTypesQuery.length>0) {
            const legalTypesQueryTable = generateTable(legalTypesQuery, 'id', 'name', 'juristisch_type_id');
            setLegalTypesType(legalTypesQueryTable);
        }
    }, [legalTypesQuery]);

    useEffect(() => {
        if (legalFormTypesQuery.length>0) {
            const legalFormTypesTable = generateTable(legalFormTypesQuery, 'id', 'name', 'juristisch_rechtsform_type_id');
            setLegalFormTypes(legalFormTypesTable);
        }
    }, [legalFormTypesQuery]);


    useEffect(() => {
        if (typesFetchQuery.length>0) {
            const typesTable = generateTable(typesFetchQuery, 'id', 'name', 'type_id');
            setGrundbuchTypes(typesTable);
        }
    }, [typesFetchQuery]);

    return (
        <div><CustomDialog
            header={t('importTag:infoTypes.header')}
            onHide={hideInfoDialog}
            onCancel={hideInfoDialog}
            visible={showInfoDialog}
            maximized={false}
            onClick={hideInfoDialog}>
            <div className="field">
                {katasterFortschreibungType}
            </div>
            <div className="field">
                {parcelUseType}
            </div>
            <div className="field">
                {legalTypes}
            </div>
            <div className="field">
                {legalFormTypes}
            </div>
            <div className="field">
                {grundbuchTypes}
            </div>

        </CustomDialog>
            <form id={props.id} onSubmit={handleSubmit(importData)}>
                <div className="field">
                <div className="col">
                    <FileUpload mode="advanced" ref={fileUploadRef} accept="csv/*" onSelect={customBase64Uploader}
                        chooseLabel={t('globalData:global.getCSVDialog.chooseCSV')}
                        uploadOptions={{className: 'hidden'}} cancelOptions={{className: 'hidden'}}/>
                </div>
            </div>
            <div className="field">
                <div className="col">
                    <Button type="submit" label={t('importTag:submitImport')} className="mt-2"/>

                </div>
            </div>
        </form>
        <a href="/assets/download/importAlkisTemplate.csv" download>
            {t('importTag:templateDownloader')}
        </a>
            <Button icon="pi pi-info-circle" className={'p-button-rounded  p-button-text'} onClick={showTheInfoDialog} /></div>
    );
};

